import React, { useState, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Polygon,
  Marker,
  Popup,
  useMapEvents,
  Circle,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Radio, InputNumber, Button, List } from "antd";
import { LocationIcons } from "./LocationIcons";

const DEFAULT_CENTER = [13.0827, 80.2707];
const LOCAL_STORAGE_KEY = "savedGeofences";

function PolygonPlotter({ onAddPoint }) {
  useMapEvents({
    click(e) {
      onAddPoint([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

function CirclePlotter({ onSetCenter }) {
  useMapEvents({
    click(e) {
      onSetCenter([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

const LocateMyVehicle = () => {
  const [fenceType, setFenceType] = useState("polygon");
  const [polygonPoints, setPolygonPoints] = useState([]);
  const [circleCenter, setCircleCenter] = useState(null);
  const [circleRadius, setCircleRadius] = useState(500);
  const [savedFences, setSavedFences] = useState([]);

  // Load saved geofences from localStorage on mount
  useEffect(() => {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (data) {
      try {
        setSavedFences(JSON.parse(data));
      } catch {
        setSavedFences([]);
      }
    }
  }, []);

  // Save geofences to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(savedFences));
  }, [savedFences]);

  // Polygon handlers
  const handleAddPolygonPoint = (latlng) => {
    setPolygonPoints((prev) => [...prev, latlng]);
  };
  const handleSavePolygon = () => {
    if (polygonPoints.length > 2) {
      setSavedFences((prev) => [
        ...prev,
        { type: "polygon", points: polygonPoints },
      ]);
      setPolygonPoints([]);
    }
  };
  const handleClearPolygon = () => setPolygonPoints([]);

  // Circle handlers
  const handleSetCircleCenter = (latlng) => setCircleCenter(latlng);
  const handleSaveCircle = () => {
    if (circleCenter && circleRadius > 0) {
      setSavedFences((prev) => [
        ...prev,
        { type: "circle", center: circleCenter, radius: circleRadius },
      ]);
      setCircleCenter(null);
    }
  };
  const handleClearCircle = () => setCircleCenter(null);

  // Delete geofence handler
  const handleDeleteFence = (idx) => {
    setSavedFences((prev) => prev.filter((_, i) => i !== idx));
  };

  const sendToServer = (idx) => {
    const fence = savedFences[idx];
    const fenceType = fence.type;
  };

  return (
    <div>
      <h4 style={{ marginTop: "1em" }}>Geofencing</h4>
      <Radio.Group
        value={fenceType}
        onChange={(e) => setFenceType(e.target.value)}
        style={{ marginBottom: 16 }}
      >
        <Radio.Button value="polygon">Polygon</Radio.Button>
        <Radio.Button value="circle">Radius</Radio.Button>
      </Radio.Group>
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={13}
        style={{ height: "400px", width: "100%", marginBottom: 16 }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />
        {fenceType === "polygon" && (
          <PolygonPlotter onAddPoint={handleAddPolygonPoint} />
        )}
        {fenceType === "circle" && (
          <CirclePlotter onSetCenter={handleSetCircleCenter} />
        )}
        {/* Draw current polygon */}
        {fenceType === "polygon" && polygonPoints.length > 2 && (
          <Polygon positions={polygonPoints} color="purple">
            <Popup>Current Polygon Geofence</Popup>
          </Polygon>
        )}
        {fenceType === "polygon" &&
          polygonPoints.map((point, idx) => (
            <Marker key={idx} position={point} icon={LocationIcons.placeIcon}>
              <Popup>
                Point {idx + 1}: {point[0].toFixed(5)}, {point[1].toFixed(5)}
              </Popup>
            </Marker>
          ))}
        {/* Draw current circle */}
        {fenceType === "circle" && circleCenter && (
          <Circle center={circleCenter} radius={circleRadius} color="orange">
            <Popup>
              Center: {circleCenter[0].toFixed(5)}, {circleCenter[1].toFixed(5)}
              <br />
              Radius: {circleRadius} meters
            </Popup>
          </Circle>
        )}
        {fenceType === "circle" && circleCenter && (
          <Marker position={circleCenter} icon={LocationIcons.placeIcon}>
            <Popup>
              Center: {circleCenter[0].toFixed(5)}, {circleCenter[1].toFixed(5)}
            </Popup>
          </Marker>
        )}
        {/* Saved fences */}
        {savedFences.map((fence, idx) =>
          fence.type === "polygon" ? (
            <Polygon key={idx} positions={fence.points} color="green">
              <Popup>Saved Polygon Geofence #{idx + 1}</Popup>
            </Polygon>
          ) : (
            <Circle
              key={idx}
              center={fence.center}
              radius={fence.radius}
              color="green"
            >
              <Popup>
                Saved Circle Geofence #{idx + 1}
                <br />
                Center: {fence.center[0].toFixed(5)},{" "}
                {fence.center[1].toFixed(5)}
                <br />
                Radius: {fence.radius} meters
              </Popup>
            </Circle>
          )
        )}
      </MapContainer>
      {/* Controls */}
      {fenceType === "polygon" && (
        <div>
          <Button
            onClick={handleSavePolygon}
            disabled={polygonPoints.length < 3}
            style={{ marginRight: 8 }}
          >
            Save Polygon Geofence
          </Button>
          <Button
            onClick={handleClearPolygon}
            disabled={polygonPoints.length === 0}
          >
            Clear Current
          </Button>
        </div>
      )}
      {fenceType === "circle" && (
        <div>
          <span style={{ marginRight: 8 }}>Radius (meters):</span>
          <InputNumber
            min={1}
            value={circleRadius}
            onChange={setCircleRadius}
            style={{ width: 120, marginRight: 8 }}
          />
          <Button
            onClick={handleSaveCircle}
            disabled={!circleCenter || circleRadius <= 0}
            style={{ marginRight: 8 }}
          >
            Save Circle Geofence
          </Button>
          <Button onClick={handleClearCircle} disabled={!circleCenter}>
            Clear Current
          </Button>
        </div>
      )}
      {savedFences.length > 0 && (
        <div style={{ marginTop: 16, maxWidth: 700 }}>
          <b>Saved Geofences:</b>
          <List
            bordered
            // size="small"
            dataSource={savedFences}
            renderItem={(fence, idx) =>
              fence.type === "polygon" ? (
                <List.Item
                  actions={[
                    <Button
                      size="small"
                      danger
                      onClick={() => handleDeleteFence(idx)}
                    >
                      Delete
                    </Button>,
                    <Button size="small" onClick={() => sendToServer(idx)}>
                      Send To Server
                    </Button>,
                  ]}
                >
                  Polygon Geofence #{idx + 1} ({fence.points.length} points)
                </List.Item>
              ) : (
                <List.Item
                  actions={[
                    <Button
                      size="small"
                      danger
                      onClick={() => handleDeleteFence(idx)}
                    >
                      Delete
                    </Button>,
                    <Button size="small" onClick={() => sendToServer(idx)}>
                      Send To Server
                    </Button>,
                  ]}
                >
                  Circle Geofence #{idx + 1} (Radius: {fence.radius} meters)
                </List.Item>
              )
            }
          />
        </div>
      )}
    </div>
  );
};
export default LocateMyVehicle;
